import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useCreateSecret, type PlainSecret } from "@/queries/secret-queries";
import { TrackLocations } from "@/hooks/use-segment";
import { formatError } from "@/utils/errors";
import { type SecretScope } from "gitpod-next-api/gitpod/v1/secret_pb";
import { Upload } from "lucide-react";
import { useCallback, useMemo, useRef, useState, type FC } from "react";
import { type EnvEntry, parseEnvFile } from "@/components/secrets/parse-env-file";
import { cn } from "@/components/podkit/lib/cn";

type Props = {
    scope: SecretScope;
    open: boolean;
    onClose: () => void;
    existingSecrets: PlainSecret[];
};

export const ImportEnvFileModal: FC<Props> = ({ scope, open, onClose, existingSecrets }) => {
    const { toast } = useToast();
    const createSecret = useCreateSecret();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [entries, setEntries] = useState<EnvEntry[]>([]);
    const [fileName, setFileName] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importError, setImportError] = useState<string | null>(null);

    const existingNames = useMemo(() => new Set(existingSecrets.map((s) => s.name)), [existingSecrets]);

    const duplicates = useMemo(() => entries.filter((e) => existingNames.has(e.name)), [entries, existingNames]);

    const reset = useCallback(() => {
        setEntries([]);
        setFileName(null);
        setImportError(null);
    }, []);

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                reset();
                onClose();
            }
        },
        [onClose, reset],
    );

    const processFile = useCallback((file: File) => {
        setImportError(null);
        // Accept any text file — the parser will handle validation
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result;
            if (typeof text !== "string") {
                setImportError("Could not read file contents.");
                return;
            }
            const parsed = parseEnvFile(text);
            if (parsed.length === 0) {
                setImportError("No valid environment variables found in the file.");
                return;
            }
            setEntries(parsed);
            setFileName(file.name);
        };
        reader.onerror = () => {
            setImportError("Failed to read the file.");
        };
        reader.readAsText(file);
    }, []);

    const handleFileSelect = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) {
                processFile(file);
            }
            // Reset input so the same file can be re-selected
            e.target.value = "";
        },
        [processFile],
    );

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragging(false);
            const file = e.dataTransfer.files[0];
            if (file) {
                processFile(file);
            }
        },
        [processFile],
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const entriesToImport = useMemo(
        () => entries.filter((e) => !existingNames.has(e.name)),
        [entries, existingNames],
    );

    const handleImport = useCallback(async () => {
        setIsImporting(true);
        setImportError(null);

        let created = 0;
        const errors: string[] = [];

        for (const entry of entriesToImport) {
            try {
                await createSecret.mutateAsync({
                    secretName: entry.name,
                    plaintextValue: entry.value,
                    scope,
                    mount: { case: "environmentVariable", value: true },
                });
                created++;
            } catch (e) {
                errors.push(`${entry.name}: ${formatError(e)}`);
            }
        }

        setIsImporting(false);

        if (errors.length > 0) {
            setImportError(`Failed to import ${errors.length} secret(s):\n${errors.join("\n")}`);
            if (created > 0) {
                toast({
                    title: "Partial import",
                    description: `Created ${created} secret(s). ${errors.length} failed.`,
                });
            }
        } else {
            toast({
                title: "Secrets imported",
                description: `${created} secret(s) created. They will be available in new environments and propagated to running ones.`,
            });
            reset();
            onClose();
        }
    }, [entriesToImport, createSecret, scope, toast, reset, onClose]);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="import-env-file-modal"
                data-track-location={TrackLocations.ImportEnvFileModal}
            >
                <DialogHeader>
                    <DialogTitle>Import .env file</DialogTitle>
                    <DialogDescription>
                        Upload a .env file to create environment variable secrets in bulk.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody>
                    {importError && (
                        <div
                            className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300"
                            data-testid="import-error"
                        >
                            {importError}
                        </div>
                    )}

                    {entries.length === 0 ? (
                        <DropZone
                            isDragging={isDragging}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onClickBrowse={() => fileInputRef.current?.click()}
                        />
                    ) : (
                        <EntriesPreview
                            entries={entries}
                            fileName={fileName}
                            duplicates={duplicates}
                            isImporting={isImporting}
                            onReset={reset}
                        />
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".env,.env.*,text/plain"
                        className="hidden"
                        onChange={handleFileSelect}
                        data-testid="env-file-input"
                    />
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="outline">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="button"
                        variant="primary"
                        disabled={entriesToImport.length === 0 || isImporting}
                        loading={isImporting}
                        onClick={handleImport}
                        data-testid="import-env-submit-button"
                        data-tracking-id="import-env-file-submit"
                    >
                        {entriesToImport.length > 0 ? `Import ${entriesToImport.length} secret${entriesToImport.length !== 1 ? "s" : ""}` : "Import"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const DropZone: FC<{
    isDragging: boolean;
    onDrop: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onClickBrowse: () => void;
}> = ({ isDragging, onDrop, onDragOver, onDragLeave, onClickBrowse }) => {
    return (
        <div
            className={cn(
                "flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 transition-colors",
                isDragging
                    ? "border-content-primary bg-surface-tertiary"
                    : "border-border-light bg-surface-secondary hover:border-border-base",
            )}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            data-testid="env-file-dropzone"
        >
            <Upload className="h-8 w-8 text-content-tertiary" />
            <div className="text-center">
                <p className="text-sm text-content-secondary">
                    Drag and drop your .env file here, or{" "}
                    <button
                        type="button"
                        className="font-medium text-content-primary underline underline-offset-2 hover:text-content-secondary"
                        onClick={onClickBrowse}
                        data-testid="env-file-browse-button"
                        data-tracking-id="import-env-file-browse"
                    >
                        browse
                    </button>
                </p>
                <p className="mt-1 text-xs text-content-tertiary">Supports .env files with KEY=VALUE pairs</p>
            </div>
        </div>
    );
};

const EntriesPreview: FC<{
    entries: EnvEntry[];
    fileName: string | null;
    duplicates: EnvEntry[];
    isImporting: boolean;
    onReset: () => void;
}> = ({ entries, fileName, duplicates, isImporting, onReset }) => {
    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <p className="text-sm text-content-secondary">
                    <span className="font-medium text-content-primary">{fileName}</span>
                    <span>{` — ${entries.length} variable${entries.length !== 1 ? "s" : ""} found`}</span>
                </p>
                <Button variant="ghost" size="sm" onClick={onReset} data-testid="env-file-reset-button" data-tracking-id="import-env-file-choose-another">
                    Choose another file
                </Button>
            </div>

            {!isImporting && duplicates.length > 0 && (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-300">
                    <span>{`${duplicates.length} secret${duplicates.length !== 1 ? "s" : ""} already exist${duplicates.length === 1 ? "s" : ""} and will be skipped: `}</span>
                    <span className="font-mono">{duplicates.map((d) => d.name).join(", ")}</span>
                </div>
            )}

            <div className="max-h-[300px] overflow-y-auto rounded-xl border border-border-base bg-surface-glass">
                <table className="w-full text-sm" data-testid="env-entries-table">
                    <thead>
                        <tr className="border-b border-border-light">
                            <th className="px-4 py-2 text-left font-medium text-content-secondary">Name</th>
                            <th className="px-4 py-2 text-left font-medium text-content-secondary">Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((entry) => (
                            <tr key={entry.name} className="border-b border-border-light last:border-b-0">
                                <td className="px-4 py-2 font-mono text-content-primary">{entry.name}</td>
                                <td className="max-w-[300px] truncate px-4 py-2 font-mono text-content-tertiary">
                                    ••••••••
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
